import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthProvider';
import {
    Home,
    Calendar,
    CalendarCheck,
    Trophy,
    Users,
    DollarSign,
    Settings,
    User,
    MessageSquare,
    CreditCard,
    GraduationCap,
    TrendingUp,
} from 'lucide-react';
import type { UserRole } from '@/types';

export const MobileBottomMenu = () => {
    const { currentUser } = useAuth();
    const { t } = useTranslation();
    const role: UserRole = currentUser?.role || 'student';

    /**
     * Bottom-nav items per role — capped at FIVE so nothing is cramped or cut off
     * on a 390px screen. Each entry maps to a route that actually exists in
     * App.tsx (see the PR body for the two parent entries that point at the
     * nearest existing equivalent rather than a purpose-built page).
     */
    const getMenuItems = () => {
        const home = { label: t('nav.home'), icon: Home, to: '/dashboard' };

        switch (role) {
            case 'student':
            case 'kid':
                return [
                    home,
                    { label: t('nav.schedule'), icon: Calendar, to: '/schedule' },
                    { label: t('nav.lms'), icon: GraduationCap, to: '/lms' },
                    { label: t('nav.leaderboard'), icon: Trophy, to: '/leaderboard' },
                    { label: t('nav.profile'), icon: User, to: '/profile' },
                ];
            case 'coach':
                return [
                    { label: t('nav.today'), icon: Home, to: '/dashboard' },
                    { label: t('nav.attendance'), icon: CalendarCheck, to: '/attendance' },
                    { label: t('nav.schedule'), icon: Calendar, to: '/schedule' },
                    { label: t('nav.students'), icon: Users, to: '/students' },
                    { label: t('nav.inbox'), icon: MessageSquare, to: '/messages' },
                ];
            case 'coordinator':
                // Coordinators are branch-operations staff and get COACH PARITY for the
                // day-to-day floor: /attendance now resolves to the Mark Attendance tool
                // for them (see canMarkAttendance), and Schedule sits alongside it.
                return [
                    home,
                    { label: t('nav.attendance'), icon: CalendarCheck, to: '/attendance' },
                    { label: t('nav.schedule'), icon: Calendar, to: '/schedule' },
                    { label: t('nav.users'), icon: Users, to: '/users' },
                    { label: t('nav.finance'), icon: DollarSign, to: '/finance' },
                ];
            case 'admin':
            case 'super_admin':
                // Was SIX items (incl. Subscriptions) — trimmed to five.
                return [
                    home,
                    { label: t('nav.users'), icon: Users, to: '/users' },
                    { label: t('nav.finance'), icon: DollarSign, to: '/finance' },
                    { label: t('nav.support_inbox'), icon: MessageSquare, to: '/admin/support-inbox' },
                    { label: t('nav.settings'), icon: Settings, to: '/settings' },
                ];
            case 'parent':
                // "View As" (child switcher) moved off the bottom bar to make room;
                // it remains available in the top-bar profile dropdown and on the
                // parent dashboard.
                return [
                    home,
                    { label: t('nav.schedule'), icon: Calendar, to: '/schedule' },
                    { label: t('nav.progress'), icon: TrendingUp, to: '/performance' },
                    { label: t('nav.payments'), icon: CreditCard, to: '/family-wallet' },
                    { label: t('nav.inbox'), icon: MessageSquare, to: '/messages' },
                ];
            default:
                return [
                    home,
                    { label: t('nav.schedule'), icon: Calendar, to: '/schedule' },
                    { label: t('nav.lms'), icon: GraduationCap, to: '/lms' },
                    { label: t('nav.leaderboard'), icon: Trophy, to: '/leaderboard' },
                    { label: t('nav.profile'), icon: User, to: '/profile' },
                ];
        }
    };

    const items = getMenuItems();


    // SECURITY: Don't show navigation for pending/unapproved accounts
    const isPendingApproval = currentUser?.status === 'pending';

    if (isPendingApproval) {
        return null; // Hide entire mobile menu for pending accounts
    }

    return (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe">
            <div className="flex justify-around items-center h-16">
                {items.map((item) => {
                    const Icon = item.icon;


                    // Regular nav items - only render if item has 'to' property
                    if (!('to' in item)) return null;

                    return (
                        <NavLink
                            key={item.label}
                            to={item.to}
                            className={({ isActive }) => `
                                flex flex-col items-center justify-center w-full h-full min-h-[44px]
                                space-y-1 touch-manipulation
                                ${isActive ? 'text-primary' : 'text-gray-500 hover:text-gray-900'}
                            `}
                        >
                            <Icon className="h-5 w-5" />
                            <span className="text-[10px] font-medium">{item.label}</span>
                        </NavLink>
                    );
                })}
            </div>
        </div>
    );
};
